import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AgGridAngular } from 'ag-grid-angular';
import {
  ColDef,
  GridApi,
  GridReadyEvent,
  RowSelectedEvent,
  CellClickedEvent,
  RowClickedEvent,
  SelectionChangedEvent,
  FirstDataRenderedEvent,
  RowDragEndEvent
} from 'ag-grid-community';

import {
  IsgGridConfig,
  DEFAULT_GRID_CONFIG,
  DEFAULT_COL_DEF,
  mergeGridConfig,
  toGridOptions
} from '../../models/grid-config.model';

/**
 * ISG Grid Component
 *
 * A standardized wrapper for AG-Grid that enforces consistent theming,
 * configuration, and behavior across the application.
 *
 * @example
 * <isg-grid
 *   [rowData]="data"
 *   [columnDefs]="columns"
 *   [config]="{ height: '500px', pagination: true }"
 *   (rowSelected)="onRowSelected($event)"
 *   (gridReady)="onGridReady($event)">
 * </isg-grid>
 */
@Component({
  selector: 'isg-grid',
  standalone: true,
  imports: [CommonModule, AgGridAngular],
  template: `
    <div class="isg-grid-container" [style.height]="mergedConfig.height">
      <ag-grid-angular
        #agGrid
        class="ag-theme-balham"
        [style.height]="mergedConfig.height"
        [style.width]="'100%'"
        [rowData]="rowData"
        [columnDefs]="columnDefs"
        [defaultColDef]="mergedDefaultColDef"
        [pagination]="mergedConfig.pagination ?? true"
        [paginationPageSize]="mergedConfig.pageSize"
        [paginationPageSizeSelector]="mergedConfig.pageSizeOptions"
        [rowSelection]="mergedConfig.rowSelection"
        [animateRows]="mergedConfig.animateRows ?? true"
        [suppressCellFocus]="false"
        [enableCellTextSelection]="mergedConfig.enableCellTextSelection ?? false"
        [suppressHorizontalScroll]="mergedConfig.suppressHorizontalScroll ?? false"
        [rowDragManaged]="mergedConfig.rowDragManaged ?? false"
        [headerHeight]="mergedConfig.headerHeight"
        [overlayNoRowsTemplate]="noRowsTemplate"
        [overlayLoadingTemplate]="loadingTemplate"
        (gridReady)="onGridReady($event)"
        (rowSelected)="onRowSelected($event)"
        (rowClicked)="onRowClicked($event)"
        (cellClicked)="onCellClicked($event)"
        (selectionChanged)="onSelectionChanged($event)"
        (firstDataRendered)="onFirstDataRendered($event)"
        (rowDragEnd)="onRowDragEnd($event)">
      </ag-grid-angular>
    </div>
  `,
  styles: [`
    .isg-grid-container {
      width: 100%;
    }

    :host ::ng-deep .ag-theme-balham {
      --ag-header-background-color: var(--isg-grid-header-bg, #F2F4F7);
      --ag-header-foreground-color: var(--isg-grid-header-text, #284A7B);
      --ag-row-hover-color: var(--isg-grid-row-hover, #EBF5D8);
      --ag-selected-row-background-color: var(--isg-grid-row-selected, #DCE1E6);
      --ag-border-color: var(--isg-grid-border, #e6e9ed);
    }

    :host ::ng-deep .grid-action-btn {
      cursor: pointer;
      text-decoration: none;
      color: inherit;
    }

    :host ::ng-deep .grid-action-btn:hover {
      color: var(--isg-color-primary, #284A7b);
    }

    :host ::ng-deep .grid-link {
      cursor: pointer;
      color: var(--isg-color-primary, #284A7b);
      text-decoration: none;
    }

    :host ::ng-deep .grid-link:hover {
      text-decoration: underline;
    }

    :host ::ng-deep .anchor-cursor {
      cursor: pointer;
    }
  `]
})
export class IsgGridComponent implements OnInit, OnChanges {
  @ViewChild('agGrid') agGrid!: AgGridAngular;

  /** Row data to display */
  @Input() rowData: any[] = [];

  /** Column definitions */
  @Input() columnDefs: ColDef[] = [];

  /** Grid configuration */
  @Input() config: Partial<IsgGridConfig> = {};

  /** Custom default column definition */
  @Input() defaultColDef: ColDef = {};

  /** Loading state */
  @Input() loading: boolean = false;

  /** Grid ready event */
  @Output() gridReady = new EventEmitter<GridReadyEvent>();

  /** Row selected event */
  @Output() rowSelected = new EventEmitter<RowSelectedEvent>();

  /** Row clicked event */
  @Output() rowClicked = new EventEmitter<RowClickedEvent>();

  /** Cell clicked event */
  @Output() cellClicked = new EventEmitter<CellClickedEvent>();

  /** Selection changed event */
  @Output() selectionChanged = new EventEmitter<SelectionChangedEvent>();

  /** First data rendered event */
  @Output() firstDataRendered = new EventEmitter<FirstDataRenderedEvent>();

  /** Row drag end event */
  @Output() rowDragEnd = new EventEmitter<RowDragEndEvent>();

  /** API reference for external access */
  @Output() apiReady = new EventEmitter<GridApi>();

  /** Merged configuration */
  mergedConfig: IsgGridConfig = DEFAULT_GRID_CONFIG;

  /** Merged default column definition */
  mergedDefaultColDef: ColDef = DEFAULT_COL_DEF;

  /** Grid API reference */
  private gridApi: GridApi | null = null;

  /** No rows overlay template */
  noRowsTemplate = '<span class="ag-overlay-no-rows-center">No data available</span>';

  /** Loading overlay template */
  loadingTemplate = '<span class="ag-overlay-loading-center">Loading...</span>';

  ngOnInit(): void {
    this.updateConfig();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config'] || changes['defaultColDef']) {
      this.updateConfig();
    }

    if (changes['loading'] && this.gridApi) {
      if (this.loading) {
        this.gridApi.showLoadingOverlay();
      } else {
        this.gridApi.hideOverlay();
      }
    }
  }

  /**
   * Update merged configuration
   */
  private updateConfig(): void {
    this.mergedConfig = mergeGridConfig(this.config);
    this.mergedDefaultColDef = { ...DEFAULT_COL_DEF, ...this.defaultColDef };
    this.noRowsTemplate = `<span class="ag-overlay-no-rows-center">${this.mergedConfig.noRowsMessage}</span>`;
  }

  /**
   * Handle grid ready event
   */
  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;

    // Show loading overlay if loading
    if (this.loading) {
      this.gridApi.showLoadingOverlay();
    }

    this.gridReady.emit(event);
    this.apiReady.emit(event.api);
  }

  /**
   * Handle first data rendered - auto-size columns
   */
  onFirstDataRendered(event: FirstDataRenderedEvent): void {
    if (this.mergedConfig.height==undefined) {
      this.mergedConfig.height = '500px';
    }
    if (this.mergedConfig.autoSizeColumns && this.gridApi) {
      this.gridApi.sizeColumnsToFit();
    }
    this.firstDataRendered.emit(event);
  }

  /**
   * Handle row selected event
   */
  onRowSelected(event: RowSelectedEvent): void {
    this.rowSelected.emit(event);
  }

  /**
   * Handle row clicked event
   */
  onRowClicked(event: RowClickedEvent): void {
    this.rowClicked.emit(event);
  }

  /**
   * Handle cell clicked event
   */
  onCellClicked(event: CellClickedEvent): void {
    this.cellClicked.emit(event);
  }

  /**
   * Handle selection changed event
   */
  onSelectionChanged(event: SelectionChangedEvent): void {
    this.selectionChanged.emit(event);
  }

  /**
   * Handle row drag end event
   */
  onRowDragEnd(event: RowDragEndEvent): void {
    this.rowDragEnd.emit(event);
  }

  // ==========================================
  // Public API Methods
  // ==========================================

  /**
   * Get selected rows
   */
  getSelectedRows<T = any>(): T[] {
    return this.gridApi?.getSelectedRows() || [];
  }

  /**
   * Get selected nodes
   */
  getSelectedNodes(): any[] {
    return this.gridApi?.getSelectedNodes() || [];
  }

  /**
   * Clear selection
   */
  clearSelection(): void {
    this.gridApi?.deselectAll();
  }

  /**
   * Select all rows
   */
  selectAll(): void {
    this.gridApi?.selectAll();
  }

  /**
   * Refresh cells
   */
  refreshCells(): void {
    this.gridApi?.refreshCells();
  }

  /**
   * Size columns to fit container
   */
  sizeColumnsToFit(): void {
    this.gridApi?.sizeColumnsToFit();
  }

  /**
   * Auto-size all columns based on content
   */
  autoSizeAllColumns(): void {
    if (this.gridApi) {
      const allColumnIds = this.columnDefs.map(col => col.field).filter(Boolean) as string[];
      this.gridApi.autoSizeColumns(allColumnIds);
    }
  }

  /**
   * Export data to CSV
   */
  exportToCsv(fileName?: string): void {
    this.gridApi?.exportDataAsCsv({
      fileName: fileName || 'export.csv'
    });
  }

  /**
   * Set quick filter
   */
  setQuickFilter(filterText: string): void {
    this.gridApi?.setGridOption('quickFilterText', filterText);
  }

  /**
   * Get grid API
   */
  getApi(): GridApi | null {
    return this.gridApi;
  }

  /**
   * Show loading overlay
   */
  showLoading(): void {
    this.gridApi?.showLoadingOverlay();
  }

  /**
   * Hide overlay
   */
  hideOverlay(): void {
    this.gridApi?.hideOverlay();
  }

  /**
   * Show no rows overlay
   */
  showNoRows(): void {
    this.gridApi?.showNoRowsOverlay();
  }
}
